import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import { createGroup, deleteGroup, deleteGroupThread, getGroupById, getUserMe, joinGroup, leaveGroup, listGroupThreadsGlobal, listGroups, listMyGroupPosts } from '../utils/secureFunctions';
import UserLink from '../components/UserLink';

interface Group {
  id: string;
  name: string;
  description: string;
  category: string;
  isPrivate: boolean;
  coverImage?: string;
  adminId: string;
  adminName: string;
  moderators?: string[]; // Array of moderator user IDs
  members: string[];
  memberCount: number;
  createdAt: any;
}

interface GroupPost {
  id: string;
  groupId: string;
  userId: string;
  userName: string;
  userPhoto: string;
  content: string;
  imageURL?: string;
  likes: string[];
  comments: number;
  createdAt: any;
}

const categories = [
  'General',
  'Marriage',
  'Health',
  'Spiritual',
  'Finance',
  'Sex & Relationship',
  'Business',
  'Career',
  'Entertainment',
  'Kids'
];

// Category visuals: icon + colors
const categoryMeta: Record<string, { icon: string; bg: string; fg: string }> = {
  'General': { icon: '🏷️', bg: '#ECEFF1', fg: '#37474F' },
  'Marriage': { icon: '💍', bg: '#FCE4EC', fg: '#AD1457' },
  'Health': { icon: '🩺', bg: '#E8F5E9', fg: '#2E7D32' },
  'Spiritual': { icon: '🙏', bg: '#F3E5F5', fg: '#6A1B9A' },
  'Finance': { icon: '💰', bg: '#E0F2F1', fg: '#00695C' },
  'Sex & Relationship': { icon: '❤️', bg: '#FFF3E0', fg: '#E65100' },
  'Business': { icon: '📈', bg: '#E3F2FD', fg: '#1565C0' },
  'Career': { icon: '🧭', bg: '#EDE7F6', fg: '#4527A0' },
  'Entertainment': { icon: '🎬', bg: '#FFF8E1', fg: '#8D6E63' },
  'Kids': { icon: '👶', bg: '#F1F8E9', fg: '#558B2F' },
};

function renderCategoryBadge(category: string) {
  const meta = categoryMeta[category] || categoryMeta['General'];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        backgroundColor: meta.bg,
        color: meta.fg,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span aria-hidden>{meta.icon}</span>
      <span>{category}</span>
    </span>
  );
}

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [myGroups, setMyGroups] = useState<Group[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 1024; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 1024); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Group details moved to a dedicated page for better UX
  const [activeTab, setActiveTab] = useState<'discover' | 'myGroups'>('discover');
  // Discoverability controls
  const [discoverSort, setDiscoverSort] = useState<'trending' | 'new'>('trending');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Threads discoverability
  type ThreadView = 'trending' | 'new' | 'active' | 'mine';
  interface GroupThread { id: string; groupId: string; groupName?: string; groupCategory?: string; userId: string; userName: string; userPhoto: string; title: string; body: string; repliesCount: number; createdAt: any; pinned?: boolean; locked?: boolean; lastReplyAt?: any; }
  const [threadView, setThreadView] = useState<ThreadView>('trending');
  const [threads, setThreads] = useState<GroupThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [myPosts, setMyPosts] = useState<GroupPost[]>([]);
  const [isSuper, setIsSuper] = useState(false);

  useEffect(() => {
    if (!auth.currentUser) return;
    getUserMe()
      .then((me) => setIsSuper((me.profile as { role?: string } | undefined)?.role === 'super_admin'))
      .catch(() => {});
  }, []);

  const handleDeleteGroup = async (g: Group) => {
    if (!window.confirm(`Delete the group "${g.name}" with all its posts and discussions? This cannot be undone.`)) return;
    try {
      await deleteGroup(g.id);
      setGroups((prev) => prev.filter((x) => x.id !== g.id));
      setMyGroups((prev) => prev.filter((x) => x.id !== g.id));
      setThreads((prev) => prev.filter((t) => t.groupId !== g.id));
    } catch (e: any) { alert(e?.message || 'Failed to delete group'); }
  };

  const handleDeleteThread = async (id: string) => {
    if (!window.confirm('Delete this forum thread and all its replies?')) return;
    try {
      await deleteGroupThread(id);
      setThreads((prev) => prev.filter((t) => t.id !== id));
    } catch (e: any) { alert(e?.message || 'Failed to delete thread'); }
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await listGroups({ sort: discoverSort, limit: 200 });
        if (!active) return;
        const loaded = (res.groups || []).map((g: any) => ({ ...g, members: Array.isArray(g.members) ? g.members : [] }));
        setGroups(loaded as Group[]);
        if (auth.currentUser) {
          setMyGroups((loaded as Group[]).filter((g) => g.members.includes(auth.currentUser!.uid)));
        }
      } catch (e) {
        console.error('Failed to load groups', e);
      }
    };
    load();
    return () => { active = false; };
  }, [discoverSort]);

  // Load forum threads for discover: trending/new/active (global) or mine (user-specific)
  useEffect(() => {
    setLoadingThreads(true);
    let active = true;
    const load = async () => {
      try {
        const res = await listGroupThreadsGlobal(threadView);
        if (!active) return;
        setThreads(res.threads as GroupThread[]);
      } catch (e) {
        console.warn('Failed to load threads', e);
      } finally {
        if (active) setLoadingThreads(false);
      }
    };
    load();
    return () => { active = false; };
  }, [threadView]);

  // Load my group posts for quick access
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setMyPosts([]); return; }
    let active = true;
    const load = async () => {
      try {
        const res = await listMyGroupPosts();
        if (!active) return;
        setMyPosts((res.posts || []).slice(0, 20) as GroupPost[]);
      } catch (e) {
        console.warn('Failed to load my posts', e);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  // Auto-join via invite links: /groups?join=GROUP_ID
  useEffect(() => {
    const tryAutoJoin = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const joinId = params.get('join');
        if (!joinId || !auth.currentUser) return;
        const res = await getGroupById(joinId);
        const gdata = res.group as Group;
        if (!gdata) return;
        await joinGroup(gdata.id);
        navigate(`/groups/${gdata.id}`);
        // Clean the join param from URL without full reload
        const url = new URL(window.location.href);
        url.searchParams.delete('join');
        window.history.replaceState({}, '', url.toString());
        alert('Joined group');
      } catch (e) {
        console.warn('Auto-join failed', e);
      }
    };
    tryAutoJoin();
  }, [navigate]);

  const handleJoinGroup = async (groupId: string) => {
    if (!auth.currentUser) return;

    try {
      const res = await joinGroup(groupId);
      if (res.group?.members?.includes(auth.currentUser.uid)) {
        alert('You are already a member of this group');
      }
    } catch (error) {
      console.error('Failed to join group:', error);
      alert('Failed to join group');
    }
  };

  const handleLeaveGroup = async (groupId: string) => {
    if (!auth.currentUser) return;

    try {
      await leaveGroup(groupId);
    } catch (error) {
      console.error('Failed to leave group:', error);
      alert('Failed to leave group');
    }
  };

  return (
    <div style={styles.container}>
      <TopNavigation />
      <LeftNavSidebar />
      <RightWidgetsSidebar />
      <div style={{ ...styles.contentArea, ...(isMobile ? styles.contentAreaMobile : {}) }}>
      <header style={styles.pageHeader}>
        <h1 style={styles.logo}>Groups</h1>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          + Create Group
        </button>
      </header>

      <div style={styles.subtitle}>
        <p style={styles.subtitleText}>Connect with like-minded believers 🤝</p>
      </div>

      <div style={styles.tabs}>
        <button 
          onClick={() => setActiveTab('discover')} 
          style={{...styles.tab, ...(activeTab === 'discover' ? styles.activeTab : {})}}
        >
          Discover Groups
        </button>
        <button 
          onClick={() => setActiveTab('myGroups')} 
          style={{...styles.tab, ...(activeTab === 'myGroups' ? styles.activeTab : {})}}
        >
          My Groups ({myGroups.length})
        </button>
      </div>

      <main style={styles.main}>
        {activeTab === 'discover' && (
          <>
            {/* Discoverability controls: Search, Category filter, Sort tabs */}
            <div style={styles.discoverControls}>
              <input
                type="text"
                placeholder="Search groups…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={styles.categorySelect}>
                <option value="All">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div style={styles.sortTabs}>
                <button onClick={() => setDiscoverSort('trending')} style={{...styles.sortTab, ...(discoverSort === 'trending' ? styles.sortTabActive : {})}}>Trending</button>
                <button onClick={() => setDiscoverSort('new')} style={{...styles.sortTab, ...(discoverSort === 'new' ? styles.sortTabActive : {})}}>New</button>
              </div>
            </div>

            <div style={{ ...styles.groupGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
              {(() => {
                // Apply filters client-side to avoid index requirements
                const q = (searchQuery || '').toLowerCase();
                const filtered = groups.filter((g) => {
                  const catOk = categoryFilter === 'All' || g.category === categoryFilter;
                  if (!catOk) return false;
                  if (!q) return true;
                  const hay = `${g.name} ${g.description}`.toLowerCase();
                  return hay.includes(q);
                });
                if (filtered.length === 0) return <p style={styles.emptyText}>No groups match your filters.</p>;
                return filtered.map((group) => (
                  <GroupCard 
                    key={group.id} 
                    group={group} 
                    onJoin={handleJoinGroup}
                    onLeave={handleLeaveGroup}
                    onView={(g) => navigate(`/groups/${g.id}`)}
                    isMember={group.members.includes(auth.currentUser?.uid || '')}
                    onDelete={isSuper ? handleDeleteGroup : undefined}
                  />
                ));
              })()}
            </div>

            {/* Threads discover section */}
            <section style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>Forum Threads</h3>
                <div style={styles.sortTabs}>
                  <button onClick={() => setThreadView('trending')} style={{...styles.sortTab, ...(threadView === 'trending' ? styles.sortTabActive : {})}}>Trending</button>
                  <button onClick={() => setThreadView('new')} style={{...styles.sortTab, ...(threadView === 'new' ? styles.sortTabActive : {})}}>New</button>
                  <button onClick={() => setThreadView('active')} style={{...styles.sortTab, ...(threadView === 'active' ? styles.sortTabActive : {})}}>Active</button>
                  <button onClick={() => setThreadView('mine')} style={{...styles.sortTab, ...(threadView === 'mine' ? styles.sortTabActive : {})}}>My Threads</button>
                </div>
              </div>
              <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* Re-use category filter and search for threads */}
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={styles.categorySelect}>
                  <option value="All">All Categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Search threads…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              {loadingThreads ? (
                <p style={styles.emptyText}>Loading threads…</p>
              ) : (
                <div style={{ ...styles.threadGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                  {(() => {
                    const q = (searchQuery || '').toLowerCase();
                    const filtered = threads.filter((t) => {
                      const catOk = categoryFilter === 'All' || t.groupCategory === categoryFilter;
                      if (!catOk) return false;
                      if (!q) return true;
                      const hay = `${t.title} ${t.body} ${t.userName} ${t.groupName || ''}`.toLowerCase();
                      return hay.includes(q);
                    });
                    if (filtered.length === 0) return <p style={styles.emptyText}>No threads match your filters.</p>;
                    return filtered.slice(0, 24).map((t) => (
                      <div key={t.id} style={styles.threadCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <ImageWithFallback src={t.userPhoto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                          <div>
                            <UserLink userId={t.userId} as="strong" style={{ fontSize: 13 }}>{t.userName}</UserLink>
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{t.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}</div>
                          </div>
                        </div>
                        <h4 style={{ margin: '8px 0 6px' }}>{t.title}</h4>
                        <p style={{ margin: 0, color: 'var(--color-text)' }}>{t.body.length > 140 ? `${t.body.slice(0, 140)}…` : t.body}</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--color-text-muted)' }}>
                            {t.groupCategory ? renderCategoryBadge(t.groupCategory) : null}
                            <span>in {t.groupName || 'Group'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--color-text-muted)' }}>
                            <span>🧵 {t.repliesCount || 0}</span>
                            {t.pinned && <span>📌</span>}
                            {t.locked && <span>🔒</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => navigate(`/groups/${t.groupId}`)} style={styles.viewButton}>Open Group</button>
                          <button onClick={() => navigate(`/groups/${t.groupId}?threadId=${t.id}`)} style={styles.viewButton}>Open Thread</button>
                          {(isSuper || t.userId === auth.currentUser?.uid) && (
                            <button onClick={() => handleDeleteThread(t.id)} style={styles.deleteButton}>Delete</button>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </section>

            {/* My recent group posts */}
            {auth.currentUser && (
              <section style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h3 style={{ margin: 0 }}>My Group Posts</h3>
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{myPosts.length} recent</span>
                </div>
                {myPosts.length === 0 ? (
                  <p style={styles.emptyText}>You haven't posted in any group yet.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {myPosts.map((p) => (
                      <div key={p.id} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, background: 'var(--color-surface)' }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{p.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}</div>
                        <div style={{ marginTop: 6 }}>{p.content.length > 160 ? `${p.content.slice(0, 160)}…` : p.content}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
                          <span>❤️ {(p.likes || []).length} • 💬 {p.comments || 0}</span>
                          <button onClick={() => navigate(`/groups/${p.groupId}`)} style={styles.viewButton}>Open Group</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {activeTab === 'myGroups' && (
          <div style={{ ...styles.groupGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
            {myGroups.length === 0 ? (
              <p style={styles.emptyText}>You haven't joined any groups yet.</p>
            ) : (
              myGroups.map((group) => (
                <GroupCard 
                  key={group.id} 
                  group={group} 
                  onJoin={handleJoinGroup}
                  onLeave={handleLeaveGroup}
                  onView={(g) => navigate(`/groups/${g.id}`)}
                  isMember={true}
                  onDelete={isSuper ? handleDeleteGroup : undefined}
                />
              ))
            )}
          </div>
        )}
      </main>

      {showCreateModal && (
        <CreateGroupModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Group detail modal replaced by dedicated route */}
      </div>
    </div>
  );
};

interface GroupCardProps {
  group: Group;
  onJoin: (id: string) => void;
  onLeave: (id: string) => void;
  onView: (group: Group) => void;
  isMember: boolean;
  onDelete?: (group: Group) => void;
}

const GroupCard: React.FC<GroupCardProps> = ({ group, onJoin, onLeave, onView, isMember, onDelete }) => {
  return (
    <div style={styles.groupCard}>
      <div style={styles.groupCover}>
        <ImageWithFallback src={group.coverImage} alt={group.name} style={styles.coverImage} />
        {group.isPrivate && <span style={styles.privateBadge}>🔒 Private</span>}
      </div>
      <div style={styles.groupContent}>
        <h3 style={styles.groupName}>{group.name}</h3>
  <div>{renderCategoryBadge(group.category)}</div>
        <p style={styles.groupDesc}>{group.description}</p>
        <div style={styles.groupStats}>
          <span>👥 {group.memberCount} members</span>
        </div>
        <div style={styles.groupActions}>
          <button onClick={() => onView(group)} style={styles.viewButton}>View</button>
          {isMember ? (
            <button onClick={() => onLeave(group.id)} style={styles.leaveButton}>Leave</button>
          ) : (
            <button onClick={() => onJoin(group.id)} style={styles.joinButton}>Join</button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(group)} style={styles.deleteButton}>Delete</button>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateGroupModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!auth.currentUser) return;
    if (!name.trim() || !description.trim()) return;
    const vn = validateContent(name);
    if (!vn.isValid) { alert(vn.message); return; }
    const vd = validateContent(description);
    if (!vd.isValid) { alert(vd.message); return; }

    setCreating(true);
    try {
      const me = await getUserMe();
      const userName = me.profile?.name || 'Anonymous';

      await createGroup({
        name: filterProfanity(name),
        description: filterProfanity(description),
        category,
        isPrivate,
        adminName: userName,
      });

      onClose();
    } catch (error) {
      console.error('Failed to create group:', error);
      alert('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Create New Group</h3>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Group Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Young Adults Bible Study"
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={styles.select}>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your group..."
              style={styles.textarea}
              rows={4}
            />
          </div>

          <div style={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="private"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              style={styles.checkbox}
            />
            <label htmlFor="private" style={styles.checkboxLabel}>Make this a private group</label>
          </div>

          <button onClick={handleCreate} disabled={creating || !name.trim() || !description.trim()} style={styles.submitButton}>
            {creating ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
};


const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  contentArea: {
    paddingTop: '16px',
    marginLeft: '300px',
    marginRight: '340px',
    paddingLeft: '20px',
    paddingRight: '20px',
    boxSizing: 'border-box',
  },
  contentAreaMobile: {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 30px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  createButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-300)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  subtitle: {
    backgroundColor: 'var(--color-primary-300)',
    padding: '15px',
    textAlign: 'center',
  },
  subtitleText: {
    margin: 0,
    fontSize: '16px',
    color: 'var(--color-text)',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    padding: '20px 10px',
    backgroundColor: 'var(--color-surface)',
    borderBottom: '2px solid var(--color-border)',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '10px 24px',
    backgroundColor: 'transparent',
    color: 'var(--color-text-muted)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  activeTab: {
    backgroundColor: 'var(--color-primary-600)',
    color: 'var(--color-surface)',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 10px 80px',
    boxSizing: 'border-box',
  },
  discoverControls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 2,
    minWidth: 220,
    padding: '10px 12px',
    borderRadius: 8,
    border: '2px solid var(--color-border)',
    fontSize: 14,
  },
  categorySelect: {
    flex: 1,
    minWidth: 200,
    padding: '10px 12px',
    borderRadius: 8,
    border: '2px solid var(--color-border)',
    fontSize: 14,
    background: 'var(--color-surface)',
    color: 'var(--color-text)'
  },
  sortTabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 4,
    background: 'var(--color-background-alt)'
  },
  sortTab: {
    padding: '8px 12px',
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    color: 'var(--color-text)',
    cursor: 'pointer' as const,
  },
  sortTabActive: {
    background: 'var(--color-primary-600)',
    color: 'var(--color-surface)'
  },
  groupGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
  threadGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
  emptyText: {
    textAlign: 'center',
    color: 'var(--color-text-muted)',
    fontSize: '16px',
    padding: '40px',
    gridColumn: '1 / -1',
  },
  groupCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
  },
  groupCover: {
    position: 'relative',
    height: '150px',
    overflow: 'hidden',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  privateBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'var(--color-surface)',
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  groupContent: {
    padding: '20px',
  },
  groupName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--color-primary)',
    marginBottom: '5px',
  },
  groupCategory: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    marginBottom: '10px',
  },
  groupDesc: {
    fontSize: '14px',
    color: 'var(--color-text)',
    marginBottom: '15px',
    lineHeight: '1.4',
  },
  groupStats: {
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    marginBottom: '15px',
  },
  groupActions: {
    display: 'flex',
    gap: '10px',
  },
  viewButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--color-background-alt)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  joinButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--color-primary-600)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  leaveButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--color-border)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  deleteButton: {
    padding: '10px 12px',
    backgroundColor: 'transparent',
    color: '#B91C1C',
    border: '1px solid #FCA5A5',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid var(--color-border)',
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-text)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    padding: '0',
  },
  modalContent: {
    padding: '20px',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: 'var(--color-text)',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  checkbox: {
    marginRight: '8px',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: 'var(--color-text)',
  },
  submitButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  groupDetailDesc: {
    fontSize: '16px',
    color: 'var(--color-text)',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  postInputSection: {
    marginBottom: '30px',
  },
  postTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-border)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '10px',
  },
  postButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'var(--color-primary-600)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  postsSection: {
    borderTop: '2px solid var(--color-border)',
    paddingTop: '20px',
  },
  postsSectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--color-text)',
    marginBottom: '15px',
  },
  groupPostCard: {
    backgroundColor: 'var(--color-background-alt)',
    borderRadius: '12px',
    padding: '15px',
    marginBottom: '15px',
  },
  threadCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 12,
    border: '1px solid var(--color-border)',
    padding: 14,
  },
  postHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  postUserName: {
    fontSize: '14px',
    color: 'var(--color-text)',
  },
  postTime: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  postContent: {
    fontSize: '14px',
    color: 'var(--color-text)',
    marginBottom: '10px',
    lineHeight: '1.4',
  },
  postActions: {
    display: 'flex',
    gap: '15px',
    fontSize: '13px',
    color: 'var(--color-text-muted)',
  },
  notMemberText: {
    textAlign: 'center',
    color: 'var(--color-text-muted)',
    fontSize: '16px',
    padding: '40px 20px',
  },
  adminPanel: {
    marginTop: '30px',
    padding: '20px',
    backgroundColor: '#FFF3E0',
    borderRadius: '12px',
    border: '2px solid var(--color-accent)',
  },
  adminPanelTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#E65100',
    marginBottom: '15px',
  },
  adminButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  adminButton: {
    padding: '10px 16px',
    backgroundColor: 'var(--color-accent)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  membersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  memberItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: 'var(--color-background-alt)',
    borderRadius: '8px',
  },
  memberAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  memberName: {
    fontSize: '15px',
    color: 'var(--color-text)',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: 'var(--color-accent)',
    color: 'var(--color-surface)',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  memberActionButton: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-surface)',
  },
};

export default Groups;
